import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { rupiah, toDateInput } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Wallet, Landmark, Smartphone, ListOrdered } from "lucide-react";

const TIPE = [
  { v: "kas", l: "Kas / Tunai" },
  { v: "bank", l: "Rekening Bank" },
  { v: "ewallet", l: "E-Wallet" },
  { v: "lainnya", l: "Lainnya" },
];
const tl = (v) => TIPE.find((x) => x.v === v)?.l || v;
const Icon = ({ t }) => (t === "bank" ? <Landmark size={16} /> : t === "ewallet" ? <Smartphone size={16} /> : <Wallet size={16} />);

export default function AkunPage() {
  const { user } = useAuth();
  const isBisnis = user?.workspace_type !== "pribadi";
  const base = isBisnis ? "/kas-bank" : "/pribadi";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { nama: "", tipe: "bank", saldo_awal: 0, tanggal_saldo_awal: "", no_rekening: "", keterangan: "" };
  const [form, setForm] = useState(empty);

  const load = async () => { const r = await api.get("/akun"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it, tanggal_saldo_awal: toDateInput(it.tanggal_saldo_awal) || "", no_rekening: it.no_rekening || "", keterangan: it.keterangan || "" }); setOpen(true); };

  const save = async () => {
    if (!form.nama?.trim()) { toast.error("Nama akun wajib diisi"); return; }
    const payload = { nama: form.nama, tipe: form.tipe, saldo_awal: Number(form.saldo_awal) || 0, tanggal_saldo_awal: form.tanggal_saldo_awal || null,
      no_rekening: form.no_rekening || "", keterangan: form.keterangan || "" };
    try {
      if (editId) await api.put(`/akun/${editId}`, payload);
      else await api.post("/akun", payload);
      toast.success(editId ? "Diperbarui" : "Akun tersimpan"); setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delOne = async (id) => {
    if (!window.confirm("Hapus akun ini?")) return;
    try { await api.delete(`/akun/${id}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const totalSaldo = items.reduce((s, x) => s + Number(x.saldo || 0), 0);
  const byTipe = TIPE.map((t) => ({ ...t, total: items.filter((a) => a.tipe === t.v).reduce((s, a) => s + Number(a.saldo || 0), 0) })).filter((t) => items.some((a) => a.tipe === t.v));

  return (
    <>
      <PageHeader title={isBisnis ? "Akun Kas & Bank" : "Akun / Dompet"} subtitle={isBisnis ? "Kas tunai, rekening bank & e-wallet usaha (dipakai bersama semua pabrik). Saldo dihitung otomatis dari pemasukan, pengeluaran, transfer, utang & piutang." : "Kelola kas, rekening bank & e-wallet. Saldo dihitung otomatis dari transaksi & transfer."} actions={
        <Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-akun-btn"><Plus size={14} className="mr-1" /> Tambah Akun</Button>
      } />
      <div className="p-4 md:p-8 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card-flat p-5" data-testid="akun-total"><div className="text-[11px] uppercase tracking-widest text-slate-500">Total Cash Semua Akun</div><div className={`mt-1 font-mono text-2xl ${totalSaldo < 0 ? "text-rose-700" : "text-slate-900"}`}>{rupiah(totalSaldo)}</div></div>
          {byTipe.map((t) => (
            <div key={t.v} className="card-flat p-5"><div className="text-[11px] uppercase tracking-widest text-slate-500">{t.l}</div><div className="mt-1 font-mono text-xl text-slate-800">{rupiah(t.total)}</div></div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="akun-grid">
          {items.length === 0 && <div className="text-slate-400">Belum ada akun.</div>}
          {items.map((a) => (
            <div key={a.id} className="card-flat p-5" data-testid={`akun-card-${a.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-slate-600 min-w-0"><span className="h-8 w-8 shrink-0 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center"><Icon t={a.tipe} /></span>
                  <span className="text-sm font-medium truncate">{a.nama}</span>
                  {a.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 shrink-0" data-testid="akun-default-badge">Default</span>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(a)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-akun-${a.id}`}><Pencil size={14} /></button>
                  {!a.is_default && <button onClick={() => delOne(a.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-akun-${a.id}`}><Trash2 size={14} className="text-rose-600" /></button>}
                </div>
              </div>
              <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-400">{tl(a.tipe)}{a.no_rekening ? ` · ${a.no_rekening}` : ""}</div>
              <div className={`font-mono text-xl font-semibold mt-1 ${Number(a.saldo) < 0 ? "text-rose-700" : ""}`} data-testid={`akun-saldo-${a.id}`}>{rupiah(a.saldo)}</div>
              <div className="text-[11px] text-slate-400 mt-1">Saldo awal: {rupiah(a.saldo_awal)}{a.tanggal_saldo_awal ? ` per ${toDateInput(a.tanggal_saldo_awal)}` : ""}</div>
              {a.total_masuk != null && <div className="text-[11px] text-slate-500 mt-0.5">Masuk {rupiah(a.total_masuk)} · Keluar {rupiah(a.total_keluar)}</div>}
              <Link to={`${base}/akun/${a.id}/mutasi`} className="inline-flex items-center gap-1 mt-3 text-xs brand-text hover:underline" data-testid={`mutasi-akun-${a.id}`}><ListOrdered size={13}/> Lihat Mutasi ({a.jumlah_transaksi ?? 0})</Link>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="akun-dialog">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Akun</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Nama Akun <span className="text-rose-500">*</span></Label><Input value={form.nama} onChange={upd("nama")} placeholder="mis. Kas Tunai / BCA Usaha / GoPay" data-testid="akun-nama" /></div>
            <div className="space-y-1.5"><Label>Tipe</Label>
              <Select value={form.tipe} onValueChange={(v) => setForm((f) => ({ ...f, tipe: v }))}>
                <SelectTrigger data-testid="akun-tipe"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPE.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>No. Rekening / Akun</Label><Input value={form.no_rekening} onChange={upd("no_rekening")} placeholder="opsional" data-testid="akun-norek" /></div>
            <div className="space-y-1.5"><Label>Saldo Awal (Rp)</Label><CurrencyInput value={form.saldo_awal} onValueChange={upd("saldo_awal")} data-testid="akun-saldo-awal" /></div>
            <div className="space-y-1.5"><Label>Per Tanggal</Label><Input type="date" value={form.tanggal_saldo_awal || ""} onChange={upd("tanggal_saldo_awal")} data-testid="akun-tgl-saldo" /></div>
            <p className="col-span-1 md:col-span-2 text-[11px] text-slate-500 -mt-2">Isi saldo sesuai uang/rekening pada tanggal tersebut. Transaksi <b>sebelum</b> tanggal ini tidak dihitung lagi ke saldo akun (cocok untuk data lama). Kosongkan bila saldo awal berlaku sejak transaksi pertama.</p>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button><Button onClick={save} className="brand-btn rounded-sm" data-testid="akun-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
